<template>
  <!--
    Um torneio = um CARTAZ pregado na página do caderno (6.6). Um por página, porque a lista de
    cards atravessando a lombada foi vetada pelo autor (03/09) — no caderno, cada página é uma
    coisa.

    A primeira versão era papel FRIO com quatro linhas de rótulo e três travessões: um aviso de
    mural, não um cartaz de torneio. O que mudou (04/09):

    - o papel é `--papel-luz`, quente, e o cartaz é PREGADO — dois pregos de latão na tarja;
    - o tier virou SELO redondo, com a cor da escada de raridade (tier 1 cinza → tier 5 ouro):
      é a única coisa da página que diz "este vale mais que o outro", e valia uma caixinha;
    - a contagem regressiva anda **de segundo em segundo** e é o maior número do cartaz. Era o
      dado que decide se o jogador entra agora ou depois, impresso no tamanho de legenda;
    - a premiação virou PÓDIO: o campeão numa faixa dourada, os outros três em fichas. Prêmio de
      vara não imprime mais "2º —  3º —  4-10 —": diz que é prêmio único e cala a boca;
    - o quadro vazio deixou de ser uma frase solta em itálico e virou moldura pontilhada com
      chamada ("pesque agora e abra o quadro").
  -->
  <article class="cartaz" :class="estado">
    <div class="cartaz__tarja">
      <span class="cartaz__prego cartaz__prego--esq" aria-hidden="true" />
      <span class="cartaz__estado">{{ textoDoEstado }}</span>
      <span class="cartaz__prazo t-numero">{{ periodo }}</span>
      <span class="cartaz__prego cartaz__prego--dir" aria-hidden="true" />
    </div>

    <div class="cartaz__corpo">
      <header class="cartaz__cabecalho">
        <!-- O selo do tier: a mesma leitura do rank do funcionário (D26.3) — cor é degrau. -->
        <span class="cartaz__selo" :style="{ '--selo': corDoTier }">
          <strong class="t-numero">{{ competition.tier }}</strong>
          <small>TIER</small>
        </span>

        <div class="cartaz__identidade">
          <h3 class="cartaz__nome">{{ competition.name }}</h3>
          <p class="cartaz__objetivo">
            <FishIcon nome="bullseye" tamanho="miudo" />
            <span>{{ tipoDaCompeticao }}</span>
          </p>
        </div>
      </header>

      <!-- O RELÓGIO. É o que decide se o jogador entra agora ou depois: ganha o corpo maior. -->
      <p class="cartaz__relogio">
        <!-- O mostrador respira enquanto o torneio corre: é o uso que o `repouso-respirar` cita. -->
        <FishIcon
          nome="clock"
          tamanho="pequeno"
          class="cartaz__relogio-icone"
          :class="{ 'repouso-respira': emAndamento }"
        />
        <span class="cartaz__relogio-rotulo">{{ textoDoRelogio }}</span>
        <span v-if="!acabou" class="cartaz__contagem">
          <span v-for="parte in partesDoTempo" :key="parte.unidade" class="cartaz__parte">
            <strong class="t-numero">{{ parte.valor }}</strong>
            <small>{{ parte.unidade }}</small>
          </span>
        </span>
      </p>

      <section class="cartaz__premios">
        <p class="cartaz__rotulo">
          <span>{{ $t('competitions.prizes') }}</span>
          <span class="cartaz__moeda">{{ tipoDoPremio }}</span>
        </p>

        <p class="cartaz__campeao">
          <FishIcon nome="trophy" tamanho="pequeno" class="cartaz__trofeu" />
          <span class="cartaz__lugar">1º</span>
          <strong>{{ premioDoCampeao }}</strong>
        </p>

        <ul v-if="!premioUnico" class="cartaz__demais">
          <li v-for="casa in demaisPremios" :key="casa.lugar" :style="{ '--medalha': casa.cor }">
            <span class="cartaz__lugar">{{ casa.lugar }}</span>
            <span>{{ casa.valor }}</span>
          </li>
        </ul>
        <p v-else class="cartaz__so-campeao">{{ $t('competitions.rewards.soleWinner') }}</p>
      </section>

      <section class="cartaz__ranking">
        <p class="cartaz__rotulo">
          <span>{{ $t('competitions.ranking') }}</span>
          <span class="cartaz__contagem-inscritos t-numero">{{ classificados.length }}/{{ premiados }}</span>
        </p>

        <ol v-if="classificados.length">
          <li
            v-for="(posto, i) in classificados"
            :key="posto.id"
            :style="{ '--medalha': corDoPosto(i) }"
          >
            <span class="cartaz__posto t-numero">{{ i + 1 }}</span>
            <span class="cartaz__jogador">{{ posto.player?.user?.username || $t('competitions.unknownPlayer') }}</span>
            <span class="cartaz__pontos t-numero">{{ pontuacao(posto.number) }}</span>
          </li>
        </ol>

        <div v-else class="cartaz__quadro-vazio">
          <FishIcon nome="users" tamanho="pequeno" />
          <p>{{ $t('competitions.noParticipants') }}</p>
          <small v-if="!acabou">{{ $t('competitions.beFirst') }}</small>
        </div>
      </section>
    </div>
  </article>
</template>

<script>
import { FishIcon } from '@/shared/ui';

/**
 * A cor do tier, na escada de raridade do jogo (tokens.css). O tier vai de 1 a 5 — é o
 * `inteiroEntre(tierMinimo, 5)` do `@fishbay/domain` — e a cor é a mesma escada que o peixe e o
 * item usam, para "tier 5" ler como lendário sem precisar de legenda.
 */
const COR_DO_TIER = {
  1: 'var(--raridade-comum)',
  2: 'var(--raridade-incomum)',
  3: 'var(--raridade-rara)',
  4: 'var(--raridade-epica)',
  5: 'var(--raridade-lendaria)',
};

/** Ouro, prata e bronze — nas cores da família do jogo, não nas do metal. */
const OURO = 'var(--ouro)';
const PRATA = 'var(--cor-neutra)';
const BRONZE = 'var(--madeira-clara)';

/** Quantos o backend premia — o `PREMIADOS` de `@fishbay/domain`. É o tamanho do quadro. */
const PREMIADOS = 10;

/** A escada de prêmio do backend: o 1º leva tudo, e daí desce. */
const CASAS = [
  { lugar: '2º', fator: 0.5, cor: PRATA },
  { lugar: '3º', fator: 0.2, cor: BRONZE },
  { lugar: '4–10', fator: 0.05, cor: 'var(--tinta-fraca)' },
];

export default {
  name: 'TorneioCartaz',
  components: { FishIcon },
  props: {
    competition: { type: Object, required: true },
    ranking: { type: Array, default: () => [] },
  },
  data() {
    // O relógio anda AQUI. O `remainingTime` vem calculado do adaptador e só se renova quando o
    // modal refaz o fetch — de minuto em minuto. Sem um tique local, uma contagem em "2m 14s"
    // ficava parada 60 segundos, que é o contrário do que uma contagem regressiva deve parecer.
    const agora = Date.now();
    return { agora, lido: agora, tique: null, premiados: PREMIADOS };
  },
  computed: {
    /** Quanto falta, em ms, já descontado o tempo que o cartaz passou aberto na tela. */
    restanteEmMs() {
      const bruto = this.competition.status.remainingTime || 0;
      return Math.max(0, bruto - (this.agora - this.lido));
    },
    acabou() {
      return this.competition.status.hasEnded || this.restanteEmMs <= 0;
    },
    emAndamento() {
      return this.competition.status.hasStarted && !this.acabou;
    },
    estado() {
      if (!this.competition.status.hasStarted) return 'cartaz--em-breve';
      if (!this.acabou) return 'cartaz--ativo';
      return 'cartaz--encerrado';
    },
    textoDoEstado() {
      if (!this.competition.status.hasStarted) return this.$t('competitions.status.upcoming');
      if (!this.acabou) return this.$t('competitions.status.active');
      return this.$t('competitions.status.finished');
    },
    textoDoRelogio() {
      if (this.acabou) return this.$t('competitions.finished');
      return this.competition.status.hasStarted
        ? this.$t('competitions.endsIn')
        : this.$t('competitions.startsIn');
    },
    /**
     * A contagem em duas casas, valor e unidade separados: o número é display grande e a unidade
     * é legenda. "3h 33m" numa fonte só é uma string; separado, é um mostrador.
     */
    partesDoTempo() {
      const u = (chave) => this.$t(`competitions.units.${chave}`);
      const total = Math.floor(this.restanteEmMs / 1000);
      const dias = Math.floor(total / 86400);
      const horas = Math.floor(total / 3600) % 24;
      const minutos = Math.floor(total / 60) % 60;
      const segundos = total % 60;

      if (dias > 0) return [{ valor: dias, unidade: u('d') }, { valor: horas, unidade: u('h') }];
      if (horas > 0) return [{ valor: horas, unidade: u('h') }, { valor: minutos, unidade: u('m') }];
      return [{ valor: minutos, unidade: u('m') }, { valor: segundos, unidade: u('s') }];
    },
    /**
     * O período impresso na tarja, como num cartaz de verdade: de tal dia a tal dia.
     *
     * `timeZone: 'UTC'` não é detalhe. O backend grava o início em meia-noite **UTC**
     * (`inicio.setUTCHours(0,0,0,0)`) e o driver lê com `timezone: 'Z'`. Formatado no fuso
     * local, em UTC-3 a meia-noite de 05/09 vira 21h de 04/09 e o cartaz anunciava o torneio
     * começando um dia antes do que o banco diz — com a contagem regressiva, que é calculada
     * em ms, discordando da tarja logo acima dela.
     */
    periodo() {
      const dia = (valor) => new Date(valor).toLocaleDateString(this.$i18n?.locale || undefined, {
        day: '2-digit',
        month: '2-digit',
        timeZone: 'UTC',
      });
      if (!this.competition.initDate || !this.competition.endDate) return '';
      return `${dia(this.competition.initDate)} – ${dia(this.competition.endDate)}`;
    },
    corDoTier() {
      return COR_DO_TIER[this.competition.tier] || 'var(--tinta-fraca)';
    },
    tipoDaCompeticao() {
      const tipos = {
        fishCaught: this.$t('competitions.types.fishCaught'),
        weightFish: this.$t('competitions.types.weightFish'),
        experience: this.$t('competitions.types.experience'),
        coins: this.$t('competitions.types.coins'),
      };
      return tipos[this.competition.type] || this.competition.type;
    },
    tipoDoPremio() {
      const tipos = {
        coins: this.$t('competitions.rewards.coins'),
        paralleliums: this.$t('competitions.rewards.paralleliums'),
        rod: this.$t('competitions.rewards.rod'),
      };
      return tipos[this.competition.rewardType] || this.competition.rewardType;
    },
    /** Vara é prêmio de um só: não há meia vara para o segundo colocado. */
    premioUnico() {
      return this.competition.rewardType === 'rod';
    },
    premioDoCampeao() {
      if (this.premioUnico) return this.$t('competitions.rewards.rodReward');
      return this.premio(1);
    },
    demaisPremios() {
      return CASAS.map((casa) => ({ ...casa, valor: this.premio(casa.fator) }));
    },
    /**
     * Os dez do pódio. A rota devolve até 50, e o rótulo ao lado dizia `/10` — com 23 inscritos
     * lia "23/10" e imprimia 23 linhas. Dez é o número certo dos dois lados: é o `PREMIADOS` do
     * domínio, quem realmente leva alguma coisa.
     */
    classificados() {
      return [...(this.ranking || [])].sort((a, b) => b.number - a.number).slice(0, PREMIADOS);
    },
  },
  watch: {
    // O modal refaz o fetch de minuto em minuto: cada resposta nova reancora o relógio local.
    'competition.status.remainingTime': function reancorar() {
      this.lido = Date.now();
      this.agora = this.lido;
    },
  },
  mounted() {
    this.tique = setInterval(() => {
      this.agora = Date.now();
    }, 1000);
  },
  beforeUnmount() {
    if (this.tique) clearInterval(this.tique);
  },
  methods: {
    premio(fator) {
      const { rewardType, rewardValue } = this.competition;
      const valor = Math.floor((rewardValue || 0) * fator).toLocaleString();
      if (rewardType === 'coins') return `${valor} ${this.$t('inventory.gold')}`;
      if (rewardType === 'paralleliums') return `${valor} ${this.$t('inventory.paralleliums')}`;
      return `${valor}`;
    },
    corDoPosto(indice) {
      return [OURO, PRATA, BRONZE][indice] || 'var(--tinta-fraca)';
    },
    pontuacao(numero) {
      switch (this.competition.type) {
        case 'fishCaught': return `${numero} ${this.$t('competitions.fish')}`;
        // Quilo, não grama: `fishings.weight` é kg (o Pacu é 2.50) e o placar guarda o peso
        // como veio, em decimal(12,2). Imprimindo "g", um recorde de 12,5 kg virava "12.5g".
        case 'weightFish': return `${Number(numero).toFixed(2)} kg`;
        case 'experience': return `${numero} XP`;
        case 'coins': return `${numero} ${this.$t('inventory.gold')}`;
        default: return `${numero}`;
      }
    },
  },
};
</script>

<style scoped>
/**
 * O cartaz é PAPEL QUENTE sobre a página (D2). A versão anterior usava `--papel-frio`, que é o
 * papel do caderno — cartaz e página na mesma cor, e o cartaz sumia dentro da folha.
 */
.cartaz {
  position: relative;
  border: 3px solid var(--madeira);
  background: var(--papel-luz);
  color: var(--tinta);
  font-family: var(--fonte-corpo);
  /* A página do caderno centraliza o que põe nela; o cartaz é impresso, e impresso alinha à
   * esquerda. Sem isto, no telefone o nome e o objetivo vinham centrados. */
  text-align: left;
  box-shadow: var(--sombra-1);
}

/* O fio impresso por dentro da moldura: é o que faz papel virar cartaz impresso. Como `::after` e
 * não `border` do corpo porque a tarja tem cor de fundo própria e o fio precisa passar por baixo. */
.cartaz__corpo::after {
  content: '';
  position: absolute;
  inset: var(--e-1);
  border: 1px dashed var(--papel-sombra);
  pointer-events: none;
}

/* -------------------------------------------------------------------------------------------- *
 * A TARJA — o estado, e a única cor forte do cartaz.
 * -------------------------------------------------------------------------------------------- */
.cartaz__tarja {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--e-2);
  padding: var(--e-1) var(--e-4);
  border-bottom: 3px solid var(--madeira);
  background: var(--papel-mancha);
  color: var(--tinta);
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.cartaz--em-breve .cartaz__tarja { background: var(--cor-aviso); }
.cartaz--ativo .cartaz__tarja { background: var(--cor-bom); }
.cartaz--encerrado .cartaz__tarja { background: var(--papel-mancha); color: var(--tinta-fraca); }

.cartaz__estado { flex: 1; }

.cartaz__prazo {
  font-size: var(--f-1);
  letter-spacing: 0;
  opacity: 0.75;
}

/* Os dois pregos de latão: o cartaz está PREGADO na página, e é a peça que faz o objeto. */
.cartaz__prego {
  position: absolute;
  top: 50%;
  width: 8px;
  height: 8px;
  margin-top: -4px;
  border-radius: var(--r-circulo);
  background: var(--latao);
  box-shadow: inset 0 -2px 0 var(--ouro-escuro), 0 1px 0 rgba(0, 0, 0, 0.3);
}

.cartaz__prego--esq { left: var(--e-1); }
.cartaz__prego--dir { right: var(--e-1); }

/* -------------------------------------------------------------------------------------------- *
 * O CORPO
 * -------------------------------------------------------------------------------------------- */
.cartaz__corpo {
  position: relative;
  padding: var(--e-3);
  display: flex;
  flex-direction: column;
  gap: var(--e-3);
}

.cartaz__cabecalho {
  display: flex;
  align-items: center;
  gap: var(--e-3);
}

/* O selo do tier — o mesmo desenho do selo de rank do galpão (D26.3), para ler igual. */
.cartaz__selo {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1;
  border-radius: var(--r-circulo);
  background: var(--papel);
  border: 3px solid var(--selo, var(--tinta-fraca));
  color: var(--selo, var(--tinta));
  font-family: var(--fonte-display);
}

.cartaz__selo strong { font-size: var(--f-5); }
.cartaz__selo small {
  font-size: 9px;
  letter-spacing: 1px;
  opacity: 0.8;
}

.cartaz__identidade { min-width: 0; }

.cartaz__nome {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-4);
  font-weight: 400;
  line-height: 1.15;
  word-break: break-word;
}

.cartaz__objetivo {
  margin: var(--e-1) 0 0;
  display: flex;
  /* `center` alinhava o ícone com o MEIO de um objetivo de duas linhas (telefone): o alvo tem que
   * ficar na primeira linha, como um marcador. */
  align-items: flex-start;
  gap: var(--e-1);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
}

/* -------------------------------------------------------------------------------------------- *
 * O RELÓGIO — o maior número do cartaz.
 * -------------------------------------------------------------------------------------------- */
.cartaz__relogio {
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--e-2);
  padding: var(--e-2) var(--e-3);
  background: var(--papel);
  border: 2px solid var(--papel-sombra);
  border-left: 4px solid var(--latao);
}

.cartaz__relogio-rotulo {
  flex: 1;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: var(--fonte-display);
}

.cartaz__contagem {
  display: flex;
  align-items: baseline;
  gap: var(--e-2);
  color: var(--latao);
}

.cartaz__parte {
  display: inline-flex;
  align-items: baseline;
  gap: 1px;
}

.cartaz__parte strong {
  font-size: var(--f-5);
  line-height: 1;
}

.cartaz__parte small {
  font-size: var(--f-1);
  opacity: 0.8;
}

.cartaz--encerrado .cartaz__relogio {
  border-left-color: var(--cor-neutra);
}

/* -------------------------------------------------------------------------------------------- *
 * A PREMIAÇÃO — pódio, não tabela.
 * -------------------------------------------------------------------------------------------- */
.cartaz__rotulo {
  margin: 0 0 var(--e-2);
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: var(--e-2);
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  text-transform: uppercase;
  letter-spacing: 1px;
  border-bottom: 1px solid var(--papel-sombra);
  padding-bottom: 2px;
}

.cartaz__moeda {
  color: var(--latao);
  letter-spacing: 0;
  text-transform: none;
}

.cartaz__campeao {
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--e-2);
  padding: var(--e-2) var(--e-3);
  background: var(--papel);
  border: 2px solid var(--ouro);
  box-shadow: var(--sombra-1);
}

.cartaz__trofeu { color: var(--ouro); }

.cartaz__campeao strong {
  flex: 1;
  font-family: var(--fonte-display);
  font-size: var(--f-3);
  color: var(--latao);
  text-align: right;
}

.cartaz__lugar {
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  color: var(--medalha, var(--latao));
  min-width: 4ch;
}

.cartaz__demais {
  list-style: none;
  margin: var(--e-2) 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: var(--e-1) var(--e-2);
  font-size: var(--f-1);
}

.cartaz__demais li {
  display: flex;
  align-items: baseline;
  gap: var(--e-1);
  padding: 2px var(--e-1);
  border-left: 3px solid var(--medalha, var(--tinta-fraca));
  background: rgba(0, 0, 0, 0.03);
}

.cartaz__so-campeao {
  margin: var(--e-2) 0 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  font-style: italic;
}

/* -------------------------------------------------------------------------------------------- *
 * O QUADRO — quem está ganhando.
 * -------------------------------------------------------------------------------------------- */
.cartaz__contagem-inscritos {
  color: var(--tinta-fraca);
  letter-spacing: 0;
}

.cartaz__ranking ol {
  list-style: none;
  margin: 0;
  padding: 0;
}

.cartaz__ranking li {
  display: flex;
  gap: var(--e-2);
  align-items: baseline;
  font-size: var(--f-2);
  padding: 3px 0;
  border-bottom: 1px dashed var(--papel-sombra);
}

.cartaz__posto {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  font-size: var(--f-1);
  border: 2px solid var(--medalha, var(--tinta-fraca));
  color: var(--medalha, var(--tinta-fraca));
  border-radius: var(--r-circulo);
}

.cartaz__jogador {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cartaz__pontos { color: var(--tinta-fraca); }

/* O vazio com moldura: um quadro esperando ser preenchido lê como convite; uma frase solta em
 * itálico lê como erro. É a mesma ideia da silhueta do peixe não descoberto (D14). */
.cartaz__quadro-vazio {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--e-1);
  padding: var(--e-3);
  border: 2px dashed var(--papel-sombra);
  color: var(--tinta-fraca);
  text-align: center;
}

.cartaz__quadro-vazio p {
  margin: 0;
  font-size: var(--f-2);
}

.cartaz__quadro-vazio small {
  font-size: var(--f-1);
  color: var(--latao);
}

/* O cartaz encerrado desbota: continua legível, para de chamar. */
.cartaz--encerrado .cartaz__corpo { opacity: 0.72; }
</style>
